import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row, Table } from "react-bootstrap";
import Pagination from "react-js-pagination";
import moment from "moment";
import swal from "sweetalert";
import { apiService } from "../../service/api.service";
import { ThreeDotSpinner } from "../loader";

const LIMIT_OPTIONS = [20, 50, 100, 200];

const okOptions = [
  { value: "", label: "All" },
  { value: "true", label: "Success only" },
  { value: "false", label: "Errors only" },
];

const bucketOptions = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
];

export const TwitterUsage = () => {
  const [summaryFilters, setSummaryFilters] = useState({
    bucket: "day",
    days: 7,
    tag: "",
    ok: "",
    status: "",
  });
  const [summaryApplied, setSummaryApplied] = useState(summaryFilters);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [detailFilters, setDetailFilters] = useState({
    days: 7,
    tag: "",
    endpoint: "",
    twitterUserId: "",
    userId: "",
    status: "",
    ok: "",
    limit: 50,
  });
  const [detailApplied, setDetailApplied] = useState(detailFilters);
  const [details, setDetails] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const summaryBuckets = useMemo(() => summaryData?.buckets || [], [summaryData]);

  useEffect(() => {
    let isActive = true;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const params = {
          bucket: summaryApplied.bucket,
          days: summaryApplied.days,
        };
        if (summaryApplied.tag) params.tag = summaryApplied.tag.trim();
        if (summaryApplied.ok) params.ok = summaryApplied.ok;
        if (summaryApplied.status) params.status = summaryApplied.status;

        const response = await apiService.getTwitterUsageSummary(params);
        if (!isActive) return;
        setSummaryData(response?.data?.data || null);
      } catch (error) {
        if (!isActive) return;
        handleError(error, "Failed to load Twitter usage summary");
      } finally {
        if (isActive) setSummaryLoading(false);
      }
    };
    fetchSummary();
    return () => {
      isActive = false;
    };
  }, [summaryApplied]);

  useEffect(() => {
    let isActive = true;
    const fetchDetails = async () => {
      setDetailLoading(true);
      try {
        const params = {
          page,
          limit: detailApplied.limit,
          days: detailApplied.days,
        };
        if (detailApplied.tag) params.tag = detailApplied.tag.trim();
        if (detailApplied.endpoint) params.endpoint = detailApplied.endpoint.trim();
        if (detailApplied.twitterUserId)
          params.twitterUserId = detailApplied.twitterUserId.trim();
        if (detailApplied.userId) params.userId = detailApplied.userId.trim();
        if (detailApplied.status) params.status = detailApplied.status;
        if (detailApplied.ok) params.ok = detailApplied.ok;

        const response = await apiService.getTwitterUsageDetails(params);
        if (!isActive) return;

        const payload = response?.data || {};
        const list = payload?.data || [];
        const meta = payload?.meta || {};

        setDetails(list);
        setTotalItems(meta.total || 0);

        if (meta.page && meta.page !== page) {
          setPage(meta.page);
        }
      } catch (error) {
        if (!isActive) return;
        handleError(error, "Failed to load Twitter usage details");
      } finally {
        if (isActive) setDetailLoading(false);
      }
    };
    fetchDetails();
    return () => {
      isActive = false;
    };
  }, [detailApplied, page]);

  const handleError = (error, fallbackMsg) => {
    if (error?.response?.status === 401) {
      swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
        localStorage.clear();
        window.location.href = "/";
      });
    } else {
      swal({
        icon: "error",
        text: error?.response?.data?.message || error?.message || fallbackMsg,
        button: "OK",
      });
    }
  };

  const applySummaryFilters = (event) => {
    event?.preventDefault();
    setSummaryApplied({ ...summaryFilters });
  };

  const applyDetailFilters = (event) => {
    event?.preventDefault();
    setPage(1);
    setDetailApplied({ ...detailFilters });
  };

  const resetSummary = () => {
    const reset = { bucket: "day", days: 7, tag: "", ok: "", status: "" };
    setSummaryFilters(reset);
    setSummaryApplied(reset);
  };

  const resetDetails = () => {
    const reset = {
      days: 7,
      tag: "",
      endpoint: "",
      twitterUserId: "",
      userId: "",
      status: "",
      ok: "",
      limit: 50,
    };
    setDetailFilters(reset);
    setDetailApplied(reset);
    setPage(1);
  };

  const getAppUserDisplay = (item) =>
    item?.userPhoneOrEmail || item?.phone_or_email || item?.user?.phone_or_email || "-";

  const getWalletDisplay = (item) =>
    item?.userWallet || item?.wallet_address || item?.user?.wallet_address || "-";

  return (
    <div className="dashboard-main-area">
      {(summaryLoading || detailLoading) && <ThreeDotSpinner />}
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <Row className="row justify-content-center align-items-center mb-3">
            <Col md={12} lg={11}>
              <div className="heading-top-area d-flex justify-content-between align-items-center">
                <h2 className="align-self-center m-0">Twitter API Usage</h2>
              </div>
            </Col>
          </Row>
        </div>

        <Row className="mb-4">
          <Col lg={11} className="mx-auto">
            <div className="bg-white p-3 rounded">
              <h5 className="mb-3">Summary (counts)</h5>
              <Form onSubmit={applySummaryFilters}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Bucket</Form.Label>
                      <Form.Select
                        value={summaryFilters.bucket}
                        onChange={(e) =>
                          setSummaryFilters((prev) => ({
                            ...prev,
                            bucket: e.target.value,
                          }))
                        }
                      >
                        {bucketOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Range (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={365}
                        value={summaryFilters.days}
                        onChange={(e) =>
                          setSummaryFilters((prev) => ({
                            ...prev,
                            days: Number(e.target.value) || 1,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Tag</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. social_gate_like"
                        value={summaryFilters.tag}
                        onChange={(e) =>
                          setSummaryFilters((prev) => ({
                            ...prev,
                            tag: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g. 200"
                        value={summaryFilters.status}
                        onChange={(e) =>
                          setSummaryFilters((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Result</Form.Label>
                      <Form.Select
                        value={summaryFilters.ok}
                        onChange={(e) =>
                          setSummaryFilters((prev) => ({
                            ...prev,
                            ok: e.target.value,
                          }))
                        }
                      >
                        {okOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col
                    md={6}
                    className="d-flex align-items-end justify-content-start gap-2"
                  >
                    <Button type="submit" variant="primary">
                      Apply
                    </Button>
                    <Button variant="outline-secondary" onClick={resetSummary}>
                      Reset
                    </Button>
                  </Col>
                  {summaryData?.from && summaryData?.to && (
                    <Col md={6} className="d-flex align-items-end justify-content-end">
                      <div className="text-end small text-muted">
                        Range: {moment(summaryData.from).format("YYYY-MM-DD")} to{" "}
                        {moment(summaryData.to).format("YYYY-MM-DD")}
                      </div>
                    </Col>
                  )}
                </Row>
              </Form>

              <div className="table-responsive mt-3">
                <Table bordered hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Total</th>
                      <th>Success</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryBuckets.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No data
                        </td>
                      </tr>
                    )}
                    {summaryBuckets.map((row) => (
                      <tr key={row._id}>
                        <td>{row._id}</td>
                        <td>{row.count}</td>
                        <td>{row.okCount}</td>
                        <td>{row.errorCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={11} className="mx-auto">
            <div className="bg-white p-3 rounded">
              <h5 className="mb-3">Detailed calls</h5>
              <Form onSubmit={applyDetailFilters}>
                <Row className="g-3">
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Range (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={365}
                        value={detailFilters.days}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            days: Number(e.target.value) || 1,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Tag</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Tag"
                        value={detailFilters.tag}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            tag: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Endpoint</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="/2/users/..."
                        value={detailFilters.endpoint}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            endpoint: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Twitter User ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="source user id"
                        value={detailFilters.twitterUserId}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            twitterUserId: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>App User ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Mongo user id"
                        value={detailFilters.userId}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            userId: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g. 200"
                        value={detailFilters.status}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Result</Form.Label>
                      <Form.Select
                        value={detailFilters.ok}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            ok: e.target.value,
                          }))
                        }
                      >
                        {okOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Per page</Form.Label>
                      <Form.Select
                        value={detailFilters.limit}
                        onChange={(e) =>
                          setDetailFilters((prev) => ({
                            ...prev,
                            limit: Number(e.target.value) || 50,
                          }))
                        }
                      >
                        {LIMIT_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col
                    md={4}
                    className="d-flex align-items-end justify-content-start gap-2"
                  >
                    <Button type="submit" variant="primary">
                      Apply
                    </Button>
                    <Button variant="outline-secondary" onClick={resetDetails}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>

              <div className="table-responsive mt-3">
                <Table bordered hover className="align-middle">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Tag</th>
                      <th>Endpoint</th>
                      <th>Status</th>
                      <th>OK</th>
                      <th>Twitter User</th>
                      <th>User Contact</th>
                      <th>Wallet Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No records
                        </td>
                      </tr>
                    )}
                    {details.map((item) => (
                      <tr key={item._id}>
                        <td>
                          {item.createdAt
                            ? moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss")
                            : "-"}
                        </td>
                        <td>{item.tag || "-"}</td>
                        <td className="text-break">{item.endpoint || item.url}</td>
                        <td>{item.status ?? "-"}</td>
                        <td>{item.ok ? "Yes" : "No"}</td>
                        <td>{item.twitterUserId || "-"}</td>
                        <td>{getAppUserDisplay(item)}</td>
                        <td>{getWalletDisplay(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {totalItems > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination
                    activePage={page}
                    itemsCountPerPage={detailApplied.limit}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    itemClass="page-item"
                    linkClass="page-link"
                    onChange={(p) => {
                      if (p !== page) setPage(p);
                    }}
                  />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
